import type { UserProfile } from '../types/auth';
import { apiService } from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: UserProfile | null;
  accessToken: string;
}

class AuthService {
  // 1. LOGIN
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    localStorage.removeItem('auth_token');

    const response = await apiService.post<LoginResponse>('/auth/login', credentials);

    const token = response.accessToken;
    if (!token) throw new Error('No access token received');

    // Temporarily save token so the next call (/me) can use it
    localStorage.setItem('auth_token', token);

    // Get Full Profile (ensure we have role, IDs, etc.)
    // We prefer fetching from /me to ensure we get the latest merged profile

    return {
      user: response.user!,
      accessToken: token,
    };
  }

  // 2. REGISTER
  async register(data: Record<string, unknown>): Promise<UserProfile> {
    // Just pass the data to backend. Backend handles role assignment (Patient)
    return await apiService.post<UserProfile>('/auth/signup', data);
  }

  // 3. GET CURRENT USER (Session Restoration)
  async getCurrentUser(): Promise<UserProfile> {
    return await apiService.get<UserProfile>('/auth/me');
  }

  async refreshAccessToken(): Promise<string> {
    const response = await apiService.post<{ accessToken: string }>('/auth/refresh');

    if (!response.accessToken) {
      throw new Error('No access token received');
    }

    localStorage.setItem('auth_token', response.accessToken);
    return response.accessToken;
  }

  // 4. AVATAR UPLOAD (3-step S3 presigned URL flow)
  async initiateAvatarUpload(
    fileName: string,
    contentType: string
  ): Promise<{ uploadUrl: string; s3Key: string }> {
    return apiService.post('/auth/avatar/initiate', { fileName, contentType });
  }

  async confirmAvatarUpload(s3Key: string): Promise<UserProfile> {
    return apiService.patch('/auth/avatar/confirm', { s3Key });
  }

  async uploadAvatar(file: File): Promise<UserProfile> {
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error('Only JPEG, PNG, and WEBP images are allowed.');
    }
    if (file.size > MAX_SIZE) {
      throw new Error('Image must be smaller than 5MB.');
    }

    // Step 1: Initiate
    const { uploadUrl, s3Key } = await this.initiateAvatarUpload(file.name, file.type);

    // Step 2: Upload directly to S3
    const s3Response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!s3Response.ok) {
      throw new Error('Failed to upload image. Please try again.');
    }

    // Step 3: Confirm
    return this.confirmAvatarUpload(s3Key);
  }

  // 6. LOGOUT
  async logout(): Promise<string> {
    return apiService.post('/auth/logout');
  }
}

export const authService = new AuthService();
